/**
 * StatusBadge — semantic status chip (success / warning / danger / info / neutral).
 *
 * Accepts either an explicit semantic `status` or a raw domain `value` string
 * (case status, invoice status, job status …) which is mapped to a semantic
 * tier via {@link statusToSemantic}. The visible label defaults to the value
 * with separators humanized.
 */
import type { CSSProperties } from 'react';
import { STATUS_TINTS, type SemanticStatus } from '../theme';

/** Map a raw domain status string to a semantic color tier. */
export function statusToSemantic(value: string): SemanticStatus {
  const v = value.toLowerCase();
  if (
    [
      'success',
      'succeeded',
      'approved',
      'submitted',
      'paid',
      'enrolled',
      'confirmed',
      'closed',
      'completed',
      'resolved',
      'active',
    ].includes(v)
  ) {
    return 'success';
  }
  if (
    [
      'warning',
      'in_review',
      'in_progress',
      'pending',
      'retrying',
      'expiring',
      'partial',
      'open',
    ].includes(v)
  ) {
    return 'warning';
  }
  if (
    ['danger', 'error', 'failed', 'dead_lettered', 'breached', 'flagged', 'void', 'rejected'].includes(
      v
    )
  ) {
    return 'danger';
  }
  if (['info', 'new', 'running', 'submitted_to_state', 'assigned'].includes(v)) {
    return 'info';
  }
  return 'neutral';
}

function humanize(value: string): string {
  return value.replace(/[._]/g, ' ');
}

export interface StatusBadgeProps {
  /** Explicit semantic tier. Takes precedence over `value`. */
  status?: SemanticStatus;
  /** Raw domain status; mapped to a semantic tier when `status` is absent. */
  value?: string;
  /** Override the visible label (defaults to humanized value/status). */
  label?: string;
  style?: CSSProperties;
}

export function StatusBadge({ status, value, label, style }: StatusBadgeProps) {
  const tier: SemanticStatus = status ?? (value ? statusToSemantic(value) : 'neutral');
  const { fg, bg } = STATUS_TINTS[tier];
  const text = label ?? (value ? humanize(value) : tier);
  return (
    <span style={{ ...base, color: fg, background: bg, ...style }}>{text}</span>
  );
}

const base: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: '0.74rem',
  fontWeight: 600,
  borderRadius: 999,
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
};
